import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { getPost, readingTime } from "../lib/blogContent";
import { useBlogAnalytics, setPageMeta } from "../lib/blogAnalytics";

function Inline({ text }) {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 ? <strong key={part}>{part}</strong> : part
  );
}

function Block({ b }) {
  if (b.t === "h2")
    return (
      <h2 className="font-sans-tight font-extrabold text-[1.5rem] tracking-tight mt-12 mb-4 text-[#1A1A1A]">
        {b.c}
      </h2>
    );
  if (b.t === "quote")
    return (
      <blockquote className="border-l-[3px] border-[#E07B3A] bg-white px-6 py-4 my-6 font-serif-body italic text-[#1A1A1A] text-[1.02rem] leading-relaxed">
        {b.c}
      </blockquote>
    );
  if (b.t === "ol")
    return (
      <ol className="list-decimal pl-6 my-6 space-y-3 font-serif-body text-[1.05rem] leading-relaxed text-[#1A1A1A]">
        {b.c.map((item) => (
          <li key={item.slice(0, 40)}>
            <Inline text={item} />
          </li>
        ))}
      </ol>
    );
  return (
    <p className="font-serif-body text-[1.05rem] leading-[1.75] text-[#1A1A1A] my-5">
      <Inline text={b.c} />
    </p>
  );
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = getPost(slug);
  useBlogAnalytics(post ? slug : null);

  React.useEffect(() => {
    if (!post) return;
    window.scrollTo(0, 0);
    setPageMeta({
      title: `${post.title} · LeadRiver`,
      description: post.metaDescription,
      image: `${window.location.origin}${post.image}`,
      url: window.location.href,
    });
    return () => {
      document.title = "LeadRiver";
    };
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <main className="pt-28 pb-24" data-testid="blog-post">
        <article className="max-w-3xl mx-auto px-6">
          <p className="kicker">
            <Link to="/blog" className="hover:text-[#E07B3A]">Insights</Link>
          </p>
          <h1 className="font-sans-tight font-extrabold text-[clamp(1.9rem,3.6vw,2.9rem)] leading-[1.12] tracking-tight mt-5">
            {post.title}
          </h1>
          <p
            className="font-mono-spec text-[12px] text-[#8A8378] mt-5"
            data-testid="blog-post-meta"
          >
            By {post.author} · {post.dateLabel} · {readingTime(post)} min read
          </p>

          <img
            src={post.image}
            alt={post.imageAlt}
            className="w-full h-[320px] sm:h-[420px] object-cover mt-8 border border-[#E5E0D8]"
          />

          <div className="mt-8">
            {post.blocks.map((b, i) => (
              <Block key={`${b.t}-${i}`} b={b} />
            ))}
          </div>

          <hr className="border-[#E5E0D8] mt-12" />
          <p className="font-serif-body italic text-[14.5px] text-[#4A4A4A] mt-6">
            Written by the team at LeadRiver. We build tools that let trade
            contractors stop being their own call center.{" "}
            <a href="https://leadriver.ai" className="underline text-[#0B3D5C]">
              leadriver.ai
            </a>
          </p>

          <div
            className="mt-10 bg-white border border-[#E5E0D8] p-7 flex flex-col sm:flex-row sm:items-center gap-5"
            data-testid="blog-post-cta"
          >
            <div className="flex-1">
              <p className="font-sans-tight font-bold text-[1.05rem] tracking-tight">
                The inbox triage in this post is what LeadRiver does.
              </p>
              <p className="font-serif-body text-[13.5px] text-[#4A4A4A] mt-1">
                Drafts in your voice, nothing sent without your hand on it.
              </p>
            </div>
            <div className="flex gap-3 flex-shrink-0">
              <Link to="/" className="btn btn-ghost py-2.5 px-5 text-[13px]">
                Learn more
              </Link>
              <a href="/#founding" className="btn btn-primary py-2.5 px-5 text-[13px]">
                Founding spots
              </a>
            </div>
          </div>
        </article>
      </main>
    </div>
  );
}
